import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Shuffle, Plus, X } from 'lucide-react';
import alertService from '../../utils/alert';
import { formatDate } from '../../utils/formatDate';
import {
  fetchAssignmentRules, createAssignmentRule, updateAssignmentRule, deleteAssignmentRule,
} from '../../redux/crmSlice';
import PageHeader from '../../components/PageHeader';
import {
  PAGE, CARD, TABLE, THEAD, TH, TD, TR, INPUT, BTN_PRIMARY, BTN_SECONDARY,
  Field, Checkbox, RowActions, TableMessage, StatusPill, ListToolbar, FormModal,
} from '../../components/crm/ui';

const BLANK_RULE = { name: '', isActive: true, source: '', assigneeEmails: [] };

// Must mirror the Lead.source enum (src/modules/crm/models/Lead.js) exactly —
// autoAssign() matches source with strict case-sensitive equality, so a rule
// built against any other string can never match a real lead.
const LEAD_SOURCES = [
  'whatsapp', 'web_form', 'manual', 'bulk_import', 'instagram', 'api', 'referral',
  'conversation_flow', 'google_ads', 'meta_ads', 'tiktok_ads', 'linkedin_ads',
  'lead_scraper', 'workflow',
];

function AssignmentRuleForm({ rule, onClose }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState(() => (rule
    ? { ...rule, source: rule.source || '', assigneeEmails: [...(rule.assigneeEmails || [])] }
    : { ...BLANK_RULE, assigneeEmails: [] }));
  const [emailInput, setEmailInput] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    if (!form.assigneeEmails.includes(email)) set({ assigneeEmails: [...form.assigneeEmails, email] });
    setEmailInput('');
  };
  const removeEmail = (email) => set({ assigneeEmails: form.assigneeEmails.filter((e) => e !== email) });

  const save = async () => {
    if (!form.name.trim()) return alertService.error('Error', 'Rule name is required');
    if (form.assigneeEmails.length === 0) return alertService.error('Error', 'Add at least one assignee email');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        isActive: form.isActive,
        source: form.source || null,
        assigneeEmails: form.assigneeEmails,
      };
      if (rule) {
        await dispatch(updateAssignmentRule({ id: rule._id, data: payload })).unwrap();
        alertService.toast('success', 'Assignment rule updated');
      } else {
        await dispatch(createAssignmentRule(payload)).unwrap();
        alertService.toast('success', 'Assignment rule created');
      }
      onClose();
    } catch (err) {
      alertService.error('Error', err || 'Failed to save assignment rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title={rule ? `Edit ${(rule.name || '').trim()}` : 'New assignment rule'} onClose={onClose} onSubmit={save} saving={saving} submitLabel="Save rule" size="max-w-2xl">
      <p className="text-[11px] text-[#706E6B]">Round-robin new leads across a set of owners, optionally scoped to a source.</p>
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Rule name *">
          <input aria-label="Rule name" className={INPUT} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. WhatsApp Round Robin" />
        </Field>
        <Field label="Source (optional — blank matches any)">
          <select aria-label="Source" className={INPUT} value={form.source} onChange={(e) => set({ source: e.target.value })}>
            <option value="">Any source</option>
            {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </section>

      <Checkbox label="Active" checked={form.isActive} onChange={(v) => set({ isActive: v })} />

      <section className="space-y-3">
        <h3 className="text-[12px] font-bold text-[#706E6B] uppercase tracking-wide">Assignee emails ({form.assigneeEmails.length})</h3>
        <div className="flex gap-2">
          <input
            type="email"
            aria-label="Assignee email"
            className={`${INPUT} flex-1`}
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
            placeholder="rep@company.com"
          />
          <button type="button" onClick={addEmail} className={BTN_SECONDARY}><Plus size={14} /> Add</button>
        </div>
        {form.assigneeEmails.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {form.assigneeEmails.map((email, idx) => (
              <span key={email} className="inline-flex items-center gap-2 h-8 px-3 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[12px] font-medium text-[#080707]">
                <span className="w-5 h-5 rounded-full bg-white border border-[#D8DDE6] flex items-center justify-center text-[10px] font-bold text-[#706E6B]">{idx + 1}</span>
                {email}
                <button type="button" onClick={() => removeEmail(email)} aria-label={`Remove ${email}`} className="text-[#706E6B] hover:text-[#BA0517]"><X size={12} /></button>
              </span>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 border-2 border-dashed border-[#D8DDE6] rounded text-[12px] font-medium text-[#706E6B]">
            No assignees yet — add at least one email to round-robin between.
          </div>
        )}
      </section>
    </FormModal>
  );
}

export default function AssignmentRulesPage() {
  const dispatch = useDispatch();
  const { assignmentRules } = useSelector((state) => state.crm);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | rule
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchAssignmentRules()).finally(() => setLoading(false));
  }, [dispatch]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assignmentRules.filter((r) => {
      if (status === 'active' && !r.isActive) return false;
      if (status === 'inactive' && r.isActive) return false;
      return !q || r.name.toLowerCase().includes(q) || (r.source || '').includes(q);
    });
  }, [assignmentRules, status, search]);

  const handleDelete = async (rule) => {
    const confirmed = await alertService.confirm('Delete Rule?', `Permanently delete "${rule.name}"?`, 'danger', 'Delete');
    if (!confirmed) return;
    try {
      await dispatch(deleteAssignmentRule(rule._id)).unwrap();
      alertService.toast('success', 'Assignment rule deleted');
    } catch (err) {
      alertService.error('Error', err || 'Failed to delete assignment rule');
    }
  };

  return (
    <div className={PAGE}>
      <PageHeader
        title="Assignment Rules"
        subtitle="Automatically round-robin new leads to owners based on source."
        icon={Shuffle}
        actions={<button type="button" onClick={() => setEditing('new')} className={BTN_PRIMARY}><Plus size={15} /> New Rule</button>}
      />

      <div className={CARD}>
        <ListToolbar
          tabs={[['', 'All'], ['active', 'Active'], ['inactive', 'Inactive']]}
          value={status}
          onChange={setStatus}
          search={search}
          onSearch={setSearch}
          placeholder="Search name or source…"
        />
        <div className="overflow-x-auto">
          <table className={`${TABLE} min-w-[40rem]`}>
            <thead className={THEAD}>
              <tr className="border-b border-[#D8DDE6]">
                <th className={TH}>Name</th>
                <th className={TH}>Source</th>
                <th className={`${TH} text-right`}>Assignees</th>
                <th className={TH}>Status</th>
                <th className={TH}>Updated</th>
                <th className={TD} />
              </tr>
            </thead>
            <tbody>
              <TableMessage
                colSpan={6}
                loading={loading}
                show={loading || rows.length === 0}
                empty={assignmentRules.length === 0 ? 'No assignment rules yet. Add a rule to set up round-robin lead assignment.' : 'No assignment rules match your filters.'}
              />
              {!loading && rows.map((rule) => (
                <tr key={rule._id} className={TR}>
                  <td className={`${TD} font-semibold text-[#2E2E2E]`}>{rule.name}</td>
                  <td className={TD}>{rule.source || <span className="text-[#706E6B]">Any</span>}</td>
                  <td className={`${TD} text-right tabular-nums`}>{(rule.assigneeEmails || []).length}</td>
                  <td className={TD}><StatusPill active={rule.isActive} /></td>
                  <td className={`${TD} text-[#706E6B]`}>{formatDate(rule.updatedAt)}</td>
                  <RowActions name={(rule.name || '').trim()} onEdit={() => setEditing(rule)} onDelete={() => handleDelete(rule)} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <AssignmentRuleForm rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
